import * as Sentry from '@sentry/react';
import { FieldTextCompact, IsolateMain, Pushbutton } from '@stratumn/atomic';
import to from 'await-to-js';
import LoadingState from 'components/organization/loadingState';
import {
  Box,
  ChangePassword,
  Divider,
  Logo,
  OauthOptions,
  SignupStepper
} from 'components/ui';
import { ROUTE_GENERATE_KEYS, ROUTE_LOGIN } from 'constant/routes';
import compose from 'lodash.flowright';
import PropType from 'prop-types';
import qs from 'query-string';
import React from 'react';
import injectSheet from 'react-jss';
import { withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { SpanType, withSpanAsync } from 'tracing';
import { withRestClient } from 'wrappers';

import { AUTH_PROVIDER_STRATUMN } from '../../constant/types';
import styles from './signup.style';

export class Signup extends React.PureComponent {
  static propTypes = {
    history: PropType.object.isRequired,
    location: PropType.object.isRequired,
    classes: PropType.object.isRequired,
    client: PropType.object.isRequired
  };

  state = {
    firstName: '',
    lastName: '',
    password: null,
    submitted: false,
    loading: true,
    providers: null
  };

  componentWillMount = async () => {
    const { location, client } = this.props;
    const { invitationToken } = qs.parse(location.search);

    try {
      const { providers } = await client.checkInvite(invitationToken);
      this.setState({ providers, loading: false });
    } catch (e) {
      console.error(e);
      toast.error(e.message);
      this.setState({ loading: false });
    }
  };

  handleChange = name => event => {
    this.setState({ [name]: event.target.value });
  };

  handlePasswordChange = password => this.setState({ password });

  isValid() {
    const { firstName, lastName, password, submitted } = this.state;

    return !submitted && !!firstName && !!lastName && !!password;
  }

  signup = async () =>
    withSpanAsync('signup', SpanType.processing, async () => {
      const { firstName, lastName, password } = this.state;
      const { location, history, client } = this.props;
      const { invitationToken } = qs.parse(location.search);
      this.setState({ submitted: true });
      const [err] = await to(
        client.signup(firstName, lastName, password, invitationToken)
      );
      // client is responsible for error handling however errors in Promise
      // have to be handled hence the return;
      if (err) {
        Sentry.captureException(err);
        this.setState({ submitted: false });
        return;
      }

      history.push(ROUTE_GENERATE_KEYS);
    });

  renderStratumnProvider = () => {
    const { classes } = this.props;
    const { firstName, lastName, providers } = this.state;

    if (providers && !providers.includes(AUTH_PROVIDER_STRATUMN)) return null;

    return (
      <>
        <div className={classes.names}>
          <div className={classes.name} data-cy="first-name">
            <FieldTextCompact
              data-testid="input-firstName"
              label="First name"
              onValueChange={this.handleChange('firstName')}
              value={this.state.firstName}
            />
          </div>
          <div className={classes.name} data-cy="last-name">
            <FieldTextCompact
              data-testid="input-lastName"
              label="Last name"
              onValueChange={this.handleChange('lastName')}
              value={this.state.lastName}
            />
          </div>
        </div>
        <ChangePassword
          data-testid="input-password"
          extraExclusionTerms={[firstName, lastName]}
          onChange={this.handlePasswordChange}
        />
        <div className={classes.signupContainer}>
          <div>
            <Pushbutton
              data-testid="signup-button"
              disabled={!this.isValid()}
              onClick={this.signup}
              primary
            >
              Create ID
            </Pushbutton>
          </div>
        </div>
      </>
    );
  };

  renderDivider = () => {
    const { providers } = this.state;
    if (!providers) {
      return <Divider text="Or sign up with" />;
    }
    if (providers.includes(AUTH_PROVIDER_STRATUMN)) {
      if (providers.length === 1) {
        return null;
      }
      return <Divider text="Or sign up with" />;
    }
    return <Divider text="Sign up with" />;
  };

  render() {
    const { loading, providers } = this.state;
    const { classes } = this.props;

    if (loading) return <LoadingState fullHeight />;

    return (
      <IsolateMain>
        <SignupStepper step={0} />
        <div className={classes.logoWrapper}>
          <Logo />
        </div>
        <Box
          secondaryTitle={
            <div className={classes.signIn}>
              Already have a Stratumn Account? <a href={ROUTE_LOGIN}>Log in</a>.
            </div>
          }
        >
          {this.renderStratumnProvider()}
          {this.renderDivider()}
          <OauthOptions providers={providers} />
        </Box>
      </IsolateMain>
    );
  }
}

export default compose(withRouter, withRestClient, injectSheet(styles))(Signup);
